<template>
  <div class="lights-editor">
    <div class="lights-editor__head">
      <span>Condition lights</span>
      <span class="lights-editor__multi-select">Multi-select</span>
    </div>

    <div class="lights-editor__grid">
      <Button
        v-for="code in LIGHT_CODES"
        :key="code"
        class="lights-editor__option"
        :class="[`lights-editor__option--${code.toLowerCase()}`, { 'lights-editor__option--selected': isSelected(code) }]"
        @click="emit('toggle', code)"
      >
        <LightDot :code="code" />
        <span class="lights-editor__option-label">{{ LIGHT_LABELS[code] }}</span>
      </Button>
    </div>

    <div class="lights-editor__foot">
      <span>{{ summary }}</span>
      <Button class="lights-editor__done" text label="Done" @click="emit('close')" />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import Button from 'primevue/button';
import LightDot from '../LightDot/LightDot.vue';

// The multi-select light picker. Rendered inside a floating panel that the
// caller supplies — `LightsRow` puts it in a PrimeVue `Popover` — so this is
// just the content column and draws no panel of its own.
//
// Lights combine: a lot can be red AND blue. That is why this is a set of
// toggles and not a single choice.
//
// It used to take the light metadata and the display order as props, which made
// every caller responsible for knowing the five hues. Both are constants of the
// domain and live here now.
defineOptions({ name: 'LightsEditor' });

// Display order, which is also the order they are read out loud on the block.
const LIGHT_CODES = ['R', 'G', 'Y', 'B', 'W'];
const LIGHT_LABELS = { R: 'Red', G: 'Green', Y: 'Yellow', B: 'Blue', W: 'White' };

const props = defineProps({
  // The codes currently on the lot.
  selected: { type: Array, default: () => [] },
});

const emit = defineEmits(['toggle', 'close']);

const isSelected = (code) => props.selected.includes(code);

const summary = computed(() =>
  props.selected.length > 0 ? `${props.selected.length} selected` : 'None — uninspected'
);
</script>

<style scoped>
.lights-editor {
  min-width: 230px;
  display: flex;
  flex-direction: column;
  gap: var(--p-lights-editor-gap);
}

.lights-editor__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: var(--p-lights-editor-head-font-size);
  font-weight: var(--p-lights-editor-head-font-weight);
  letter-spacing: var(--p-lights-editor-head-letter-spacing);
  color: var(--p-lights-editor-head-color);
  text-transform: uppercase;
}

.lights-editor__multi-select {
  color: var(--p-lights-editor-multi-select-color);
  font-weight: var(--p-lights-editor-multi-select-font-weight);
  letter-spacing: var(--p-lights-editor-multi-select-letter-spacing);
}

.lights-editor__grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: var(--p-lights-editor-grid-gap);
}

/* An unselected option is dimmed rather than hidden, so the five are always in
   the same place — muscle memory beats a compacting list here. */
.p-button.lights-editor__option {
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 120ms;
  opacity: 0.55;
  font-family: inherit;
  gap: var(--p-lights-editor-option-gap);
  padding: var(--p-lights-editor-option-padding);
  border-radius: var(--p-lights-editor-option-border-radius);
  background: var(--p-lights-editor-option-background);
  border: var(--p-lights-editor-option-border-width) solid var(--p-lights-editor-option-border-color);
}

.p-button.lights-editor__option--selected {
  opacity: 1;
  background: var(--p-lights-editor-option-selected-background);
}

/* The selected ring is the light's own hue, so it reads as "this light is on"
   rather than as a generic selection. */
.p-button.lights-editor__option--selected.lights-editor__option--r {
  border-color: var(--p-lights-editor-option-r-border-color);
  box-shadow: var(--p-lights-editor-option-r-box-shadow);
}

.p-button.lights-editor__option--selected.lights-editor__option--g {
  border-color: var(--p-lights-editor-option-g-border-color);
  box-shadow: var(--p-lights-editor-option-g-box-shadow);
}

.p-button.lights-editor__option--selected.lights-editor__option--y {
  border-color: var(--p-lights-editor-option-y-border-color);
  box-shadow: var(--p-lights-editor-option-y-box-shadow);
}

.p-button.lights-editor__option--selected.lights-editor__option--b {
  border-color: var(--p-lights-editor-option-b-border-color);
  box-shadow: var(--p-lights-editor-option-b-box-shadow);
}

.p-button.lights-editor__option--selected.lights-editor__option--w {
  border-color: var(--p-lights-editor-option-w-border-color);
  box-shadow: var(--p-lights-editor-option-w-box-shadow);
}

.lights-editor__option-label {
  font-size: var(--p-lights-editor-option-label-font-size);
  font-weight: var(--p-lights-editor-option-label-font-weight);
  letter-spacing: var(--p-lights-editor-option-label-letter-spacing);
  color: var(--p-lights-editor-option-label-color);
}

.lights-editor__option--selected .lights-editor__option-label {
  color: var(--p-lights-editor-option-selected-label-color);
}

.lights-editor__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: var(--p-lights-editor-foot-padding-top);
  border-top: var(--p-lights-editor-foot-border-top-width) solid var(--p-lights-editor-foot-border-top-color);
  font-size: var(--p-lights-editor-foot-font-size);
  color: var(--p-lights-editor-foot-color);
}

.p-button.lights-editor__done {
  background: transparent;
  border: 0;
  font-family: inherit;
  padding: var(--p-lights-editor-done-padding);
  border-radius: var(--p-lights-editor-done-border-radius);
  font-size: var(--p-lights-editor-done-font-size);
  font-weight: var(--p-lights-editor-done-font-weight);
  color: var(--p-lights-editor-done-color);
}
</style>
